import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Suspense, useEffect, useMemo } from 'react';
import { useLocation, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import cn from 'classnames';
import { Box, FullBox } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { AgreementTriageModalOpener } from '~/components/AgreementTriageModal/AgreementTriageModalOpener';
import { ContentPaneSuspenseFallback } from '~/components/ContentPaneSuspenseFallback/ContentPaneSuspenseFallback';
import ErrorBoundaryPage from '~/components/ErrorBoundaryPage/ErrorBoundaryPage';
import { FullPageInvoiceOpener } from '~/components/FullPageInvoice/FullPageInvoiceOpener/FullPageInvoiceOpener';
import InAppNotificationDrawer from '~/components/InAppNotificationsDrawer/InAppNotificationsDrawer';
import { LoginErrorModal } from '~/components/LoginErrorModal';
import { MobileDeviceEnhancer } from '~/components/MobileDeviceEnhancer/MobileDeviceEnhancer';
import HomeSidebar, { getSidebarRoutes, sidebarTextsPath } from '~/components/NavigationAndStructure/HomeSidebar/HomeSidebar';
import { TopBar } from '~/components/NavigationAndStructure/TopBar/';
import { getUserMenuRoutes, userMenuRouteTextsPath } from '~/components/NavigationAndStructure/UserMenu/UserMenu';
import { RouteWrapper } from '~/components/RouteWrapper';
import { defaultAuthorizedRoute, routes, UtmParams } from '~/constants';
import { useIoContext, useOverlayContext, useStore, useStoreStatic } from '~/contexts';
import { usePersonaKYBStatus } from '~/hooks/reactQuery/queries/usePersonaKYBStatus';
import { useCreateVendorAccount } from '~/hooks/useCreateVendorAccount/useCreateVendorAccount';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useHomePageRoutesByUrl } from '~/hooks/useHomePageRoutesByUrl';
import { useInAppNotificationsBinder } from '~/hooks/useInAppNotificationsBinder';
import { useIsMobileForHomePage } from '~/hooks/useIsMobileForHomePage';
import { useNativeFilteredNavLinks } from '~/hooks/useNativeFilteredNavLinks';
import { useOnMount } from '~/hooks/useOnMount';
import { useUtmsParams } from '~/hooks/useUtmsParams';
import { useLocaleKeys } from '~/i18n';
import { importServicesRoute } from '~/pages/ServicesLibrary/CommonServicesPage/CommonServicesPage.utils';
import { readPersistedCurrentStep } from '~/pages/VendorOnboarding/hooks/useOnboardingFlow';
import { selectors } from '~/store/state-selectors';
import { TasksDrawerRoot } from '~/tasks-drawer/TasksDrawerRoot/TasksDrawerRoot';
import { relativePath, withSplat } from '~/utils/routePaths';
import { AnchorForBusinessBanner } from './AnchorForBusinessBanner';
import { MissingColumnBankAccountBanner } from './Banners';
import { BiometricNudgeBanner } from './BiometricNudgeBanner/BiometricNudgeBanner';
import styles from './HomePage.module.scss';
import HomePageProvider, { useHomePage } from './HomePageProvider';
const appRoutes = [
    {
        id: 'minisite',
        text: '',
        url: routes.minisite(undefined),
        shouldShow: true
    }
];
const SECURE_LOGIN_SET_FLAG = 'secure-login-set-success';
export const GetHomePageTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            HomePageTID: {
                HomeScreen: 'home-screen'
            }
        }
    });
function useCreateVendorAccountForClientWithReferral() {
    const getUtms = useUtmsParams();
    const createVendorAccount = useCreateVendorAccount();
    const { getState } = useStoreStatic();
    useOnMount(()=>{
        var _getState_userInfo;
        const utms = getUtms();
        const isCustomer = (_getState_userInfo = getState().userInfo) === null || _getState_userInfo === void 0 ? void 0 : _getState_userInfo.isCustomer();
        if (utms[UtmParams.Referral] && isCustomer) {
            createVendorAccount();
        }
    });
}
function useIsReturningToIncompleteVendor() {
    const userInfo = useStore((state)=>state.userInfo);
    if (userInfo == null) return false;
    const business = userInfo.business();
    if (business == null || business.postalAddress == null) return true;
    return business.description == null;
}
function LegacyPayoutsRedirect() {
    const { search } = useLocation();
    return /*#__PURE__*/ _jsx(Navigate, {
        to: {
            pathname: routes.payoutsDashboard,
            search
        },
        replace: true
    });
}
export function ActualHomePage(props) {
    var _ref;
    const { HomePageTID } = useMemo(()=>GetHomePageTID(), []);
    const { t: sidebarTexts } = useLocaleKeys(sidebarTextsPath);
    const { showSuccessToast } = useOverlayContext();
    const { t: commonTexts } = useLocaleKeys('common');
    const navigate = useNavigate();
    useCreateVendorAccountForClientWithReferral();
    const userInfo = useStore((state)=>state.userInfo);
    const { hidden } = useHomePage();
    const { localStorage } = useIoContext();
    const isVendor = useStore(selectors.user.isVendor);
    const featureFlags = useFeatureFlags();
    const { CONNECTED_APPS_PAGE, SALES_TAX } = featureFlags;
    const { t: userMenuTexts } = useLocaleKeys(userMenuRouteTextsPath);
    const allRoutes = [
        ...getSidebarRoutes({
            texts: sidebarTexts,
            CONNECTED_APPS_PAGE,
            SALES_TAX
        }),
        ...getUserMenuRoutes(userMenuTexts),
        importServicesRoute,
        ...appRoutes
    ];
    const pageLinks = useStore(selectors.filterAvailableRoutes(useNativeFilteredNavLinks(allRoutes)));
    const isMobileDevice = useIsMobileForHomePage();
    const componentMountMap = useHomePageRoutesByUrl();
    const firstName = userInfo === null || userInfo === void 0 ? void 0 : userInfo.firstName();
    const lastName = userInfo === null || userInfo === void 0 ? void 0 : userInfo.lastName();
    const hasName = !!firstName && !!lastName;
    const isClient = userInfo === null || userInfo === void 0 ? void 0 : userInfo.isCustomer();
    const hasBusiness = !!(userInfo === null || userInfo === void 0 ? void 0 : userInfo.hasBusiness());
    const claims = useStore((state)=>state.claims);
    const oldVobDone = hasName && hasBusiness;
    const returningToIncompleteVendor = useIsReturningToIncompleteVendor();
    const activeBusinessId = userInfo === null || userInfo === void 0 ? void 0 : userInfo.businessId();
    const isActiveWorkspaceCompletedVendor = activeBusinessId != null && ((_ref = userInfo === null || userInfo === void 0 ? void 0 : userInfo.workspaces()) !== null && _ref !== void 0 ? _ref : []).some((ws)=>ws.id() === activeBusinessId && ws.isVendor() && ws.isCompletelySetup());
    const isMidVendorOnboarding = readPersistedCurrentStep() != null;
    const vobDone = !isMidVendorOnboarding && (isActiveWorkspaceCompletedVendor || oldVobDone && !returningToIncompleteVendor);
    const isAnchorAdmin = useStore(selectors.user.isAnchorAdmin);
    const isSetIdentityFlow = useStore(selectors.isSetIdentityFlow);
    const isSwitchingBusiness = useStore((state)=>state.isSwitchingBusiness);
    const location = useLocation();
    const isOnLoginErrorRoute = location.pathname.includes(routes.loginError);
    useOnMount(()=>{
        if (localStorage.getItem(SECURE_LOGIN_SET_FLAG) === 'true') {
            localStorage.removeItem(SECURE_LOGIN_SET_FLAG);
            showSuccessToast(commonTexts.toasts.secureLoginSetSuccessfully(), 'secure-login-set-success-toast');
        }
    });
    useEffect(()=>{
        if (isSetIdentityFlow && !isSwitchingBusiness && !isOnLoginErrorRoute && location.pathname !== routes.vendorOnboarding) {
            navigate(routes.vendorOnboarding, {
                replace: true
            });
        }
    }, [
        isSetIdentityFlow,
        isSwitchingBusiness,
        isOnLoginErrorRoute,
        location.pathname
    ]);
    useEffect(()=>{
        if (!hidden && !isClient && !vobDone && location.pathname !== routes.vendorOnboarding) {
            navigate(routes.vendorOnboarding, {
                replace: true
            });
        }
    // WHY: re-run only on onboarding-state changes; current location is read at trigger-time. Adding location.* re-fires the onboarding redirect on every navigation.
    // eslint-disable-next-line react-hooks-static-deps/exhaustive-deps
    }, [
        hidden,
        hasBusiness,
        isClient,
        vobDone
    ]);
    function getAllNavigation(items) {
        return items.flatMap((item)=>'url' in item ? item : 'items' in item ? getAllNavigation(item.items) : []);
    }
    const shouldHideForNoBusiness = claims != null && !claims.businessId;
    if (hidden) return null;
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: !shouldHideForNoBusiness && /*#__PURE__*/ _jsxs(FullBox, {
            flexDirection: "row",
            className: cn(styles.mainContainer, {
                [styles.anchorAdmin]: isAnchorAdmin
            }),
            testId: HomePageTID.HomeScreen(),
            children: [
                /*#__PURE__*/ _jsx(FullPageInvoiceOpener, {}),
                /*#__PURE__*/ _jsx(AgreementTriageModalOpener, {}),
                /*#__PURE__*/ _jsx(InAppNotificationDrawer, {}),
                /*#__PURE__*/ _jsx(TasksDrawerRoot, {}),
                !isMobileDevice && /*#__PURE__*/ _jsx(HomeSidebar, {}),
                /*#__PURE__*/ _jsx(FullBox, {
                    flexDirection: "column",
                    className: styles.mainContent,
                    children: /*#__PURE__*/ _jsxs(FullBox, {
                        flexDirection: "column",
                        children: [
                            /*#__PURE__*/ _jsx(TopBar, {}),
                            /*#__PURE__*/ _jsxs(MobileDeviceEnhancer, {
                                enabled: isMobileDevice,
                                maxHeight: false,
                                children: [
                                    /*#__PURE__*/ _jsx(MissingColumnBankAccountBanner, {}),
                                    /*#__PURE__*/ _jsx(AnchorForBusinessBanner, {}),
                                    /*#__PURE__*/ _jsx(BiometricNudgeBanner, {})
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Box, {
                                className: styles.pageContent,
                                children: /*#__PURE__*/ _jsx(ErrorBoundaryPage, {
                                    bounded: true,
                                    children: /*#__PURE__*/ _jsx(Suspense, {
                                        fallback: /*#__PURE__*/ _jsx(ContentPaneSuspenseFallback, {}),
                                        children: /*#__PURE__*/ _jsxs(Routes, {
                                            children: [
                                                props.children,
                                                getAllNavigation(pageLinks).map((param)=>{
                                                    let { url, exact } = param;
                                                    const relative = relativePath(url, routes.home);
                                                    return /*#__PURE__*/ _jsx(Route, {
                                                        path: exact ? relative : withSplat(relative),
                                                        element: /*#__PURE__*/ _jsx(RouteWrapper, {
                                                            config: componentMountMap[url]
                                                        })
                                                    }, url);
                                                }),
                                                /*#__PURE__*/ _jsx(Route, {
                                                    path: relativePath(routes.legacyPayoutsDashboard, routes.home),
                                                    element: /*#__PURE__*/ _jsx(LegacyPayoutsRedirect, {})
                                                }),
                                                /*#__PURE__*/ _jsx(Route, {
                                                    path: "*",
                                                    element: /*#__PURE__*/ _jsx(Navigate, {
                                                        to: !isClient && !vobDone ? routes.vendorOnboarding : defaultAuthorizedRoute(isVendor),
                                                        replace: true
                                                    })
                                                })
                                            ]
                                        })
                                    })
                                })
                            }),
                            isOnLoginErrorRoute && /*#__PURE__*/ _jsx(LoginErrorModal, {})
                        ]
                    })
                })
            ]
        })
    });
}
export function HomePage(props) {
    const isVendor = useStore(selectors.user.isVendor);
    // WHY: warm the KYB cache at boot without blocking the shell — KYB consumers (dashboard, banners) render their own pending states
    usePersonaKYBStatus({
        enabled: isVendor
    });
    useInAppNotificationsBinder();
    return /*#__PURE__*/ _jsx(HomePageProvider, {
        children: /*#__PURE__*/ _jsx(ActualHomePage, _object_spread({}, props))
    });
}
